import * as React from "react";
import { BaseComponent } from "../../../../react-core/Html/BaseComponent";
import { ProcessHeader } from "./ProcessHeader";
import { ProcessLeftbar } from "./ProcessLeftbar";
import { ProcessStepperMobile } from "./ProcessStepperMobile";
import { ProcessStepper } from "./ProcessStepper";
import { RouteFragment } from "../../../../react-core/Imports";
import { Navigator } from "../../../Shared/Wizards/Entities/Navigator";
import { PartLoading } from "../../../../react-core/Html/PartLoading";
import { IProcessNaviBtnConfig, ProcessNavigateCustomBtn } from "./ProcessNavigateBtn";
import { Lightbox } from "../../../../react-core/Html/Components/Lightbox";
import { UnmountClosed } from "react-collapse";
import { ProcessErrorMsg, PROCESS_ERROR_MSG_DIV_ID } from "./ErrorMsg";
import { Scroll } from "../../../../react-core/Utils/Scroll";
import { SecuredFooter } from "../../../Dashboard/Components/SecuredFooter";

export const enum StepType {
    Standard, //default type
    Extra, // not show in the stepper
    Custom, // in the steppper but with no default button
    Confirmation, // confirmation page
}

export interface IStepConfig {
    title: string;
    url?: RouteFragment;
    component?: JSX.Element;
    path?: string;
    onStepLeave?: (from: number, to: number) => void;
    type?: StepType;
    group?: string;
    canSubmit?: () => boolean;
    onSubmit?: () => void;
    needPrint?: boolean;
}

export interface IProcessProps {
    stepsConfig: IStepConfig[];
    onSave?: (currentStep: number) => void;
    onCancel: VoidFunction;
    goBacktoFirst?: boolean;
    processTitle: string;
    subTitle?: string;
    isShowError: boolean;
    errorMsg: string[];
    canSwitchStep?: boolean;
    needSplitHeader?: {
        fromStep: number;
        splitHeaderMain: string;
        splitHeaderSub: string;
    };
    active: boolean;
    onPosting?: boolean;
    isLoading?: boolean;
    onClearError?: VoidFunction;
}

export interface IProcessState {
    currentStep: number;
    isCancelling?: boolean;
}

export class Process extends BaseComponent<IProcessProps, IProcessState> {
    constructor(props: any) {
        super(props);
        this.state = {
            currentStep: 0,
        };
    }
    static defaultProps = { isLoading: false };

    componentDidMount() {
        this.renderWizardStep(this.props.stepsConfig, true);
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (this.props.isShowError !== nextProps.isShowError && nextProps.isShowError) {
            return Scroll.scrollTop();
        }
        this.renderWizardStep(nextProps.stepsConfig, false);
    }

    private goToStep(to: number, isRedirect: boolean, stepsConfig: IStepConfig[]) {
        if (isRedirect) {
            Navigator.navigateToAndPreserveQueryParameters(stepsConfig[to].url);
            return;
        }
        this.setState({
            currentStep: to,
        });
    }

    private onStepLeave(from: number, to: number) {
        const { stepsConfig, onClearError, active } = this.props;
        if (onClearError) {onClearError();}
        if (to > from && stepsConfig[from].onStepLeave && active) {
            stepsConfig[from].onStepLeave(from, to);
        } else {
            this.goToStep(to, true, stepsConfig);
        }
    }

    private renderWizardStep(stepsConfig: IStepConfig[], isMount: boolean) {
        if (stepsConfig.length == 0) {return null;}
        const i = window.location.pathname.lastIndexOf("/");
        const path = window.location.pathname.substr(i + 1);
        const currentPath = stepsConfig.findIndex((ele) => ele.path.toLowerCase() == path.toLowerCase());
        const to =
            currentPath < 0 || (this.props.goBacktoFirst && isMount)
                ? 0
                : stepsConfig.findIndex((ele) => ele.path.toLowerCase() == path.toLowerCase());
        const isRedirect = currentPath < 0 || (currentPath !== 0 && this.props.goBacktoFirst && isMount) ? true : false;
        this.goToStep(to, isRedirect, stepsConfig);
    }

    private onCancel() {
        this.setState({ isCancelling: true });
    }

    public render(): JSX.Element {
        const { currentStep } = this.state;
        const current = this.props.stepsConfig[currentStep];
        const type = current.type || StepType.Standard;
        return this.props.isLoading ? (
            <PartLoading isLoading={true}></PartLoading>
        ) : (
            <>
                <div className="layout-process">
                    <ProcessHeader />
                    <div className="layout-process-content">
                        <div className="container">
                            <ProcessLeftbar
                                isActive={this.props.active}
                                onPosting={this.props.onPosting}
                                onCancel={() => this.onCancel()}
                                onSave={this.props.onSave ? () => this.props.onSave(currentStep) : null}
                            />
                            <div className="layout-process-page">
                                <div className="minheight-container">
                                    {!this.props.needSplitHeader ||
                                    currentStep < this.props.needSplitHeader.fromStep ? (
                                        <div className="process-heading ad d1">
                                            <h1 className={this.props.subTitle ? "mb-0" : null}>
                                                {this.props.processTitle}
                                            </h1>
                                            {this.props.subTitle && (
                                                <h3 className="text-uppercase">{this.props.subTitle}</h3>
                                            )}
                                            <ProcessStepperMobile
                                                currentStep={currentStep}
                                                config={this.props.stepsConfig}
                                            />
                                        </div>
                                    ) : (
                                        <div className="process-split-heading ad d1">
                                            <div className="split-heading-main">
                                                <h1 className={this.props.subTitle ? "mb-0" : null}>
                                                    {this.props.processTitle}
                                                </h1>
                                                {this.props.subTitle && (
                                                    <h2 className="h3 text-uppercase">{this.props.subTitle}</h2>
                                                )}
                                                <ProcessStepperMobile
                                                    currentStep={currentStep}
                                                    config={this.props.stepsConfig}
                                                />
                                            </div>
                                            <div className="split-heading-aside">
                                                <h2 className="user-name">
                                                    {this.props.needSplitHeader.splitHeaderMain}
                                                </h2>
                                                <p className="account-id">
                                                    {this.props.needSplitHeader.splitHeaderSub}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                    <div className="process-aside">
                                        <div role="main" className="process-aside-content" id="main">
                                            <div className="process-panel ad d2">
                                                <UnmountClosed isOpened={this.props.isShowError}>
                                                    <ProcessErrorMsg msg={this.props.errorMsg} />
                                                </UnmountClosed>
                                                <div key={`component-${current.title}`}>{current.component}</div>
                                                {type == StepType.Standard || type == StepType.Extra ? (
                                                    <ProcessNavigateCustomBtn
                                                        isBusy={this.props.onPosting}
                                                        needPrint={current.needPrint}
                                                        primary={
                                                            current.onSubmit && !this.props.active
                                                                ? null
                                                                : current.onSubmit && this.props.active
                                                                ? this.submitButtonConfig(currentStep)
                                                                : this.moveNextButtonConfig(currentStep)
                                                        }
                                                        secondary={this.movePreviousButtonConfig(currentStep)}
                                                    />
                                                ) : 
                                                <ProcessNavigateCustomBtn
                                                needPrint={current.needPrint}
                                            />}
                                            </div>
                                        </div>
                                        <aside>
                                            <div
                                                role="navigation"
                                                aria-label="Wizard navigaion"
                                                className="process-aside-panel ad d2"
                                                style={{ zIndex: -1 }}
                                            >
                                                <ProcessStepper
                                                    onStepLeave={(from, to) => this.onStepLeave(from, to)}
                                                    currentStep={currentStep}
                                                    config={this.props.stepsConfig}
                                                    canSwitchStep={this.props.canSwitchStep}
                                                />
                                            </div>
                                        </aside>
                                    </div>
                                </div>
                                <SecuredFooter />
                            </div>
                        </div>
                    </div>
                </div>

                {this.state.isCancelling && (
                    <Lightbox
                        show={this.state.isCancelling}
                        title={"Do you want to cancel?"}
                        unclosable={true}
                        onClose={() => this.setState({ isCancelling: false })}
                        cssWrapper="is-default-lightbox is-narrow"
                        contentCssCustom="pt-20"
                    >
                        <p className="mb-10">
                            If you cancel, you lose the information you have entered during this process.
                        </p>
                        <p>Are you sure you want to cancel?</p>

                        <button className="btn primary mr-20 mt-20" onClick={() => this.props.onCancel()}>
                            Yes
                        </button>
                        <button className="btn secondary  mt-20" onClick={() => this.setState({ isCancelling: false })}>
                            No
                        </button>
                    </Lightbox>
                )}
            </>
        );
    }

    private onSubmit(submit: () => void, canSubmit: () => boolean): void {
        if (this.props.onClearError) {this.props.onClearError();}
        if (canSubmit && !canSubmit()) {return;}
        const confirmationStepIndex = this.findConfirmationPage();

        if (confirmationStepIndex > -1) {
            this.goToStep(confirmationStepIndex, true, this.props.stepsConfig);
        }
        submit();
    }

    private findConfirmationPage(): number {
        return this.props.stepsConfig.findIndex((x) => x.type === StepType.Confirmation);
    }

    private submitButtonConfig(currentStep: number): IProcessNaviBtnConfig {
        return {
            title: "Submit",
            onClick: () =>
                this.onSubmit(
                    this.props.stepsConfig[currentStep].onSubmit,
                    this.props.stepsConfig[currentStep].canSubmit
                ),
        };
    }

    private moveNextButtonConfig(currentStep: number): IProcessNaviBtnConfig {
        const next = this.props.stepsConfig[currentStep + 1];
        return currentStep + 1 < this.props.stepsConfig.length
            ? {
                  title: (
                      <>
                          <b>Next:</b> {next.group ? `${next.group} ${next.title}` : next.title}
                      </>
                  ),
                  onClick: () => this.onStepLeave(currentStep, currentStep + 1),
              }
            : null;
    }

    private movePreviousButtonConfig(currentStep: number): IProcessNaviBtnConfig {
        return currentStep > 0
            ? { title: "Previous", onClick: () => this.onStepLeave(currentStep, currentStep - 1) }
            : null;
    }
}
